import { MAP_STORAGE_KEY, LAYER_SETTINGS_STORAGE_KEY } from "./mapConstants";
import type { LayerSettings } from "./mapTypes";
import { normalizeNauticalChartSettings } from "./mapTypes";

/**
 * Represents the persisted state of the map view
 */
export interface MapViewState {
  center: [number, number];
  zoom: number;
}

/**
 * Loads the map view state from localStorage
 * @returns The saved map view state, or null if not found or invalid
 */
export function loadMapViewState(): MapViewState | null {
  try {
    const stored = localStorage.getItem(MAP_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (
      Array.isArray(parsed.center) &&
      parsed.center.length === 2 &&
      typeof parsed.center[0] === "number" &&
      typeof parsed.center[1] === "number" &&
      Number.isFinite(parsed.center[0]) &&
      Number.isFinite(parsed.center[1]) &&
      typeof parsed.zoom === "number" &&
      Number.isFinite(parsed.zoom) &&
      parsed.zoom >= 0 &&
      parsed.zoom <= 20
    ) {
      return {
        center: [parsed.center[0], parsed.center[1]],
        zoom: parsed.zoom,
      };
    }
  } catch {
    // Invalid stored data, ignore
  }
  return null;
}

/**
 * Saves the map view state to localStorage
 * @param center - The map center coordinates [lng, lat]
 * @param zoom - The map zoom level
 */
export function saveMapViewState(center: [number, number], zoom: number): void {
  try {
    localStorage.setItem(MAP_STORAGE_KEY, JSON.stringify({ center, zoom }));
  } catch {
    // Ignore storage failures
  }
}

/**
 * Default layer settings used as fallback
 */
const DEFAULT_LAYER_SETTINGS: LayerSettings = {
  weather: true,
  harbor: true,
  crowdPoi: true,
  septic: true,
  ais: true,
  networkTiles: false,
  ice: false,
  navWarnings: false,
  forecast: false,
  heatMap: false,
  raster2025: false,
  rasterSpecial: false,
  avoinMerikartta: true,
  s57: false,
  maastokartta: false,
  combined: false,
  scoutRoutes: false,
  scoutRouting: false,
  savedPlaces: true,
  recreationSites: false,
  restriction01SpeedLimit: false,
  restriction02NoWake: false,
  restriction03NoWindsurfing: false,
  restriction04NoJetSki: false,
  restriction05NoMotorPower: false,
  restriction06NoAnchoring: false,
};

/**
 * Loads layer settings from localStorage
 * @returns The saved layer settings, or default settings if not found or invalid
 */
export function loadLayerSettings(): LayerSettings {
  try {
    const stored = localStorage.getItem(LAYER_SETTINGS_STORAGE_KEY);
    if (!stored) return DEFAULT_LAYER_SETTINGS;
    const parsed = JSON.parse(stored);
    const isBoolean = (value: unknown): value is boolean =>
      typeof value === "boolean";
    // Validate that parsed object has all required boolean properties
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      isBoolean((parsed as Record<string, unknown>).weather) &&
      isBoolean((parsed as Record<string, unknown>).harbor) &&
      isBoolean((parsed as Record<string, unknown>).crowdPoi) &&
      isBoolean((parsed as Record<string, unknown>).septic) &&
      isBoolean((parsed as Record<string, unknown>).ais) &&
      // networkTiles was added later; treat missing as valid (will use fallback)
      (typeof (parsed as Record<string, unknown>).networkTiles === "boolean" ||
        (parsed as Record<string, unknown>).networkTiles === undefined) &&
      isBoolean((parsed as Record<string, unknown>).ice) &&
      // forecast was added later; treat missing as valid (will use fallback)
      (typeof (parsed as Record<string, unknown>).forecast === "boolean" ||
        (parsed as Record<string, unknown>).forecast === undefined) &&
      isBoolean((parsed as Record<string, unknown>).heatMap) &&
      isBoolean((parsed as Record<string, unknown>).raster2025) &&
      // rasterSpecial was added later; treat missing as valid (will use fallback)
      (typeof (parsed as Record<string, unknown>).rasterSpecial === "boolean" ||
        (parsed as Record<string, unknown>).rasterSpecial === undefined) &&
      isBoolean((parsed as Record<string, unknown>).avoinMerikartta) &&
      isBoolean((parsed as Record<string, unknown>).s57) &&
      isBoolean((parsed as Record<string, unknown>).scoutRoutes) &&
      isBoolean((parsed as Record<string, unknown>).savedPlaces)
    ) {
      const storedSettings = parsed as Record<string, unknown>;
      const readBoolean = (
        key: keyof LayerSettings,
        fallback: boolean,
      ): boolean => {
        const value = storedSettings[key];
        return typeof value === "boolean" ? value : fallback;
      };
      const scoutRouting =
        typeof storedSettings.scoutRouting === "boolean"
          ? storedSettings.scoutRouting
          : storedSettings.scoutRoutes === true;
      // Treat S57 as available while loading so we do not drop a saved S57 selection
      // before MapView knows subscription state (see normalize effect there).
      return normalizeNauticalChartSettings(
        {
          weather: readBoolean("weather", DEFAULT_LAYER_SETTINGS.weather),
          harbor: readBoolean("harbor", DEFAULT_LAYER_SETTINGS.harbor),
          crowdPoi: readBoolean("crowdPoi", DEFAULT_LAYER_SETTINGS.crowdPoi),
          septic: readBoolean("septic", DEFAULT_LAYER_SETTINGS.septic),
          ais: readBoolean("ais", DEFAULT_LAYER_SETTINGS.ais),
          networkTiles: readBoolean(
            "networkTiles",
            DEFAULT_LAYER_SETTINGS.networkTiles,
          ),
          ice: readBoolean("ice", DEFAULT_LAYER_SETTINGS.ice),
          navWarnings: readBoolean(
            "navWarnings",
            DEFAULT_LAYER_SETTINGS.navWarnings,
          ),
          forecast: readBoolean("forecast", DEFAULT_LAYER_SETTINGS.forecast),
          heatMap: readBoolean("heatMap", DEFAULT_LAYER_SETTINGS.heatMap),
          raster2025: readBoolean("raster2025", DEFAULT_LAYER_SETTINGS.raster2025),
          rasterSpecial: readBoolean(
            "rasterSpecial",
            DEFAULT_LAYER_SETTINGS.rasterSpecial,
          ),
          avoinMerikartta: readBoolean(
            "avoinMerikartta",
            DEFAULT_LAYER_SETTINGS.avoinMerikartta,
          ),
          s57: readBoolean("s57", DEFAULT_LAYER_SETTINGS.s57),
          // maastokartta was added later; treat missing as valid (will use fallback)
          maastokartta: readBoolean(
            "maastokartta",
            DEFAULT_LAYER_SETTINGS.maastokartta,
          ),
          // combined was added later; treat missing as valid (will use fallback)
          combined: readBoolean("combined", DEFAULT_LAYER_SETTINGS.combined),
          scoutRoutes: readBoolean("scoutRoutes", DEFAULT_LAYER_SETTINGS.scoutRoutes),
          scoutRouting,
          savedPlaces: readBoolean("savedPlaces", DEFAULT_LAYER_SETTINGS.savedPlaces),
          recreationSites: readBoolean(
            "recreationSites",
            DEFAULT_LAYER_SETTINGS.recreationSites,
          ),
          restriction01SpeedLimit: readBoolean(
            "restriction01SpeedLimit",
            DEFAULT_LAYER_SETTINGS.restriction01SpeedLimit,
          ),
          restriction02NoWake: readBoolean(
            "restriction02NoWake",
            DEFAULT_LAYER_SETTINGS.restriction02NoWake,
          ),
          restriction03NoWindsurfing: readBoolean(
            "restriction03NoWindsurfing",
            DEFAULT_LAYER_SETTINGS.restriction03NoWindsurfing,
          ),
          restriction04NoJetSki: readBoolean(
            "restriction04NoJetSki",
            DEFAULT_LAYER_SETTINGS.restriction04NoJetSki,
          ),
          restriction05NoMotorPower: readBoolean(
            "restriction05NoMotorPower",
            DEFAULT_LAYER_SETTINGS.restriction05NoMotorPower,
          ),
          restriction06NoAnchoring: readBoolean(
            "restriction06NoAnchoring",
            DEFAULT_LAYER_SETTINGS.restriction06NoAnchoring,
          ),
        },
        true,
      );
    }
  } catch {
    // Invalid stored data, ignore
  }
  return normalizeNauticalChartSettings(DEFAULT_LAYER_SETTINGS, false);
}

/**
 * Saves layer settings to localStorage
 * @param settings - The layer settings to save
 * @param s57Available - Whether S57 chart is available (premium or dev overlay)
 */
export function saveLayerSettings(
  settings: LayerSettings,
  s57Available: boolean,
): void {
  try {
    const normalized = normalizeNauticalChartSettings(settings, s57Available);
    localStorage.setItem(
      LAYER_SETTINGS_STORAGE_KEY,
      JSON.stringify(normalized),
    );
  } catch {
    // Ignore storage failures
  }
}
